import { useReactiveVar } from '@apollo/client';

import { useMyCareerCenterInfoQuery } from 'core/graphql';
import { authState } from 'core/state';
import { UserRole } from 'context';

import { ICareerCenter, transformCareerCenter } from './transform';

interface IUseMyCareerCenter {
  /** `null` = not enrolled. This IS the membership gate (spec 0018 §3.1). */
  center: ICareerCenter | null;
  isMember: boolean;
  loading: boolean;
  error: string;
  refetch: () => void;
}

/**
 * The student's Career Center membership + overall progress.
 *
 * Skipped for anyone who is not an active-role Student (and for the demo user):
 * every career resolver rejects a non-Student LMS token (spec 0018 §3.3), so
 * running it for a Teacher/Methodist would only produce a noisy PermissionDenied
 * on every page — the Header mounts this hook app-wide to gate the menu entry.
 *
 * `isMember` is false while loading, so nothing career-related flashes before
 * membership is known.
 *
 * `cache-and-network` mirrors usePendingSurveysCount: a student enrolled by
 * staff mid-session sees the section appear on the next mount without a reload.
 */
export const useMyCareerCenter = (): IUseMyCareerCenter => {
  const { active_role, isDemoUser } = useReactiveVar(authState);

  const skip = active_role !== UserRole.STUDENT || Boolean(isDemoUser);

  const { data, loading, error, refetch } = useMyCareerCenterInfoQuery({
    fetchPolicy: 'cache-and-network',
    skip,
  });

  const center = skip ? null : transformCareerCenter(data);

  return {
    center,
    isMember: center !== null,
    loading,
    error: error?.message || '',
    refetch,
  };
};
